import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SearchIcon } from "../ui";

/**
 * ⌘K command palette (§2.7): navigation + actions + the Phase-2 client
 * index. items: [{ id, label, hint?, icon?, keywords?, searchOnly?, run }].
 * searchOnly items are hidden until the user types (keeps the idle list a
 * short page/action menu); typed results cap at 12.
 */
function CommandPalette({ open, onClose, items }) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Idle palette: pages + actions only. searchOnly items (the Phase-2
      // client index — hundreds of households) appear once the user types.
      return items.filter((item) => !item.searchOnly);
    }
    return items
      .filter((item) =>
        `${item.label} ${item.keywords || ""} ${item.hint || ""}`.toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Focus after the portal paints.
      window.setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) {
    return null;
  }

  const run = (item) => {
    onClose();
    item.run();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) {
        run(filtered[activeIndex]);
      }
    }
  };

  return createPortal(
    <div
      className="shell-palette-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="shell-palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="shell-palette__inputrow">
          <SearchIcon size={16} />
          <input
            ref={inputRef}
            className="shell-palette__input"
            placeholder="Jump to a page or action…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search pages and actions"
          />
        </div>
        <ul className="shell-palette__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="shell-palette__empty">No matches for “{query}”.</li>
          ) : (
            filtered.map((item, i) => (
              <li
                key={item.id}
                role="option"
                aria-selected={i === activeIndex}
                className={`shell-palette__item${i === activeIndex ? " shell-palette__item--active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  run(item);
                }}
              >
                {item.icon}
                {item.label}
                {item.hint ? <span className="shell-palette__item-hint">{item.hint}</span> : null}
              </li>
            ))
          )}
        </ul>
        <div className="shell-palette__foot">
          ↑↓ to navigate · Enter to open · Esc to close — type a name, email, or phone to find a
          client
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CommandPalette;
